import * as React from 'react';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { cn } from '@/lib/utils/cn';

/**
 * One row of the Discovery and Selection status cards on the engagement
 * Overview: label, figure, a meta line and the source pill, with an
 * optional link at the right of the label. The shape is the Discovery
 * dashboard's KPI tile (`kpi()` in `docs/discovery/prototype/discovery.js`)
 * at the density of the Overview's domain rows (`HealthPostureGrid`).
 * `ai` gives an AI-derived figure the AI surface tint, as the facts strip's
 * AI cell has (README §6: AI content is never styled as a plain row).
 */
export function StatusRow({
  label,
  value,
  meta,
  source,
  action,
  ai = false,
}: {
  label: string;
  value: React.ReactNode;
  meta: React.ReactNode;
  source: string;
  action?: React.ReactNode;
  ai?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-0.5 rounded border border-ink-100 px-3 py-2.5',
        ai ? 'bg-ai-surface' : 'bg-paper-0',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('t-meta', ai && 'text-ai-ink')}>{label}</span>
        {action ?? null}
      </div>
      <div
        className={cn(
          'text-body-sm font-semibold tabular-nums',
          ai ? 'text-ai-ink' : 'text-ink-900',
        )}
      >
        {value}
      </div>
      <div className={cn('text-meta', ai ? 'text-ai-ink/80' : 'text-ink-500')}>{meta}</div>
      <KpiSourcePill source={source} className="mt-1 self-start" />
    </div>
  );
}

/** The small link at the right of a row's label. */
export const STATUS_ROW_LINK =
  'text-meta font-medium text-ink-700 underline decoration-ink-300 underline-offset-2 hover:text-ink-900 hover:decoration-ink-700';
